import { Routes } from '@angular/router';
import { FeatureFlags } from '@core/constants';
import { adminOnlyGuard } from '@core/guards/admin-only/admin-only.guard';
import { authGuard } from '@core/guards/auth/auth.guard';
import { noAuthGuard } from '@core/guards/no-auth/no-auth.guard';
import { onboardGuard } from '@core/guards/onboard/onboard.guard';
import { AuthLayoutComponent } from '@core/layouts/auth-layout/auth-layout.component';
import { LandingLayoutComponent } from '@core/layouts/landing-layout/landing-layout.component';
import { NoAuthLayoutComponent } from '@core/layouts/no-auth-layout/no-auth-layout.component';
import { PublicPageLayoutComponent } from '@core/layouts/public-page-layout/public-page-layout.component';
import { roleGuard } from '@core/role-management/guards/role.guard';
import { Role } from '@core/role-management/models/role';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';

import { ForgotPasswordSuccessComponent } from './components/forgot-password/components/forgot-password-success/forgot-password-success.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { ImpersonationComponent } from './components/impersonation/impersonation.component';
import { LoginComponent } from './components/login/login/login.component';
import { RequestAccountComponent } from './components/login/request-account/request-account.component';
import { UnifiedLoginComponent } from './components/login/unified-login/unified-login.component';
import { onboardStepGuard } from './components/onboarding/onboard-step.guard';
import { OnboardingResponsiveWelcomeComponent } from './components/onboarding/onboarding-responsive-welcome/onboarding-responsive-welcome.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
import { UserCreateFirstPasswordComponent } from './components/user-create-first-password/user-create-first-password.component';
import { examplesRoutes } from './examples/examples-routing.module';

function getLoginRoutes(isRequestAccountEnabled: boolean): Routes {
  const oldLoginRoutes: Routes = [
    { path: '', redirectTo: 'login', pathMatch: 'full' },
    { path: 'login', component: LoginComponent, canActivate: [noAuthGuard] },
  ];
  const newLoginRoutes: Routes = [
    {
      path: 'request-account',
      component: RequestAccountComponent,
    },
    { path: '', redirectTo: 'login', pathMatch: 'full' },
    {
      path: 'login',
      component: UnifiedLoginComponent,
      canActivate: [noAuthGuard],
    },
  ];

  return isRequestAccountEnabled ? newLoginRoutes : oldLoginRoutes;
}

export function getRoutes(featureFlagService: FeatureFlagService): Routes {
  const requestFlag: boolean = featureFlagService.featureFlagEnabled(
    FeatureFlags.ENABLE_ACCOUNT_REQUEST
  );

  return [
    // No Auth Site routes goes here
    {
      path: '',
      component: NoAuthLayoutComponent,
      children: [
        ...getLoginRoutes(requestFlag),
        {
          path: 'forgot-password',
          component: ForgotPasswordComponent,
          canActivate: [noAuthGuard],
        },
        {
          path: 'forgot-password/success',
          component: ForgotPasswordSuccessComponent,
          canActivate: [noAuthGuard],
        },
        {
          path: 'reset-password/:token',
          component: ResetPasswordComponent,
          canActivate: [noAuthGuard],
        },
        {
          path: 'user-create-first-password/:token',
          component: UserCreateFirstPasswordComponent,
          canActivate: [noAuthGuard],
        },
        {
          path: 'onboarding/mobile-welcome',
          component: OnboardingResponsiveWelcomeComponent,
          canActivate: [onboardStepGuard],
        },
      ],
    },
    // Public Pages routes goes here
    {
      path: '',
      component: PublicPageLayoutComponent,
      children: [
        {
          path: 'track',
          loadChildren: () =>
            import('./modules/public-pages/public-pages-routing.module').then(
              (m) => m.publicPagesRoutes
            ),
        },
      ],
    },
    {
      path: '',
      component: PublicPageLayoutComponent,
      children: [
        {
          path: 'ship-lite',
          loadChildren: () =>
            import('./modules/public-pages/features/ship-lite/ship-lite-routing.module').then(
              (m) => m.shipLiteRoutes
            ),
        },
      ],
    },
    // Landing routes goes here
    {
      path: '',
      component: LandingLayoutComponent,
      children: [
        { path: '', redirectTo: 'onboarding', pathMatch: 'full' },
        {
          path: 'onboarding',
          // component: OnboardingComponent,
          loadChildren: () =>
            import('./components/onboarding/onboarding-routing.module').then(
              (m) => m.onboardingRoutes
            ),

          canActivate: [adminOnlyGuard, onboardGuard],
        },
      ],
    },
    // App routes goes here
    {
      path: '',
      component: AuthLayoutComponent,
      children: [
        { path: '', redirectTo: 'shipping/ship', pathMatch: 'full' },
        {
          path: 'redirect',
          loadComponent: () =>
            import('./components/redirect/redirect.component').then(
              (m) => m.RedirectComponent
            ),
          canActivate: [authGuard],
        },
        {
          path: 'company',
          loadChildren: () =>
            import('./modules/customer/customer-routing.module').then(
              (m) => m.customerRoutes
            ),
          canActivate: [authGuard],
        },
        {
          path: 'user-profile',
          loadComponent: () =>
            import('./modules/user-profile/features/user-profile-page/components/user-profile-page.component').then(
              (m) => m.UserProfilePageComponent
            ),
          canActivate: [authGuard],
        },
        {
          path: 'shipping',
          loadChildren: () =>
            import('./modules/shipping/shipping-routing.module').then(
              (m) => m.shippingRoutes
            ),
        },
        {
          path: 'invoices',
          loadChildren: () =>
            import('./modules/invoices/invoices-routing.module').then(
              (m) => m.invoicesRoutes
            ),
          canActivate: [roleGuard],
          data: { role: 'backend-service-invoice-view-invoices' as Role },
        },
        {
          path: 'tracking',
          loadChildren: () =>
            import('./modules/shipping/features/tracking/tracking-routing.module').then(
              (m) => m.trackingRoutes
            ),
        },
      ],
    },
    // Examples routes goes here
    {
      path: 'examples',
      children: examplesRoutes,
    },
    // No layout routes
    {
      path: 'impersonate/:accessKey',
      component: ImpersonationComponent,
    },
    // Otherwise redirect to home
    { path: '**', redirectTo: '' },
  ];
}
